import React, { useEffect, useState } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import { FIRESTORE_USERS_TABLE } from "../constants/FirebaseConstants";
import { onAuthStateChanged } from "firebase/auth";
import {
  identifyUser,
  resetUserIdentification,
  updateUserProperties,
} from "../helpers/posthogAnalytics";
import {
  STORAGE_DELIVERY_CITY,
  STORAGE_DELIVERY_COUNTRY,
  STORAGE_DELIVERY_HOUSENUMBER,
  STORAGE_DELIVERY_HOUSENUMBER_ADDITION,
  STORAGE_DELIVERY_STREET,
  STORAGE_DELIVERY_ZIP,
} from "../constants/LocalStorageConstants";

export const AuthContext = React.createContext({
  authUser: null,
  userProfile: null,
  loading: true,
});

export const useAuthContext = () => React.useContext(AuthContext);

export const AuthContextProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        const docRef = doc(db, FIRESTORE_USERS_TABLE, authUser.uid);
        const docSnap = await getDoc(docRef);
        const userProfile = docSnap.data();
        setAuthUser(authUser);
        setUserProfile(userProfile);

        identifyUser(authUser.uid, {
          email: authUser.email,
          email_verified: authUser.emailVerified,
          phone: userProfile?.phone || null,
          name: userProfile?.name || null,
          created_at: authUser.metadata?.creationTime,
          last_sign_in: authUser.metadata?.lastSignInTime,
        });

        return onSnapshot(
          doc(db, FIRESTORE_USERS_TABLE, authUser.uid),
          (doc) => {
            setUserProfile(doc.data());
            setLoading(false);
          },
        );
      } else {
        setAuthUser(null);
        setUserProfile(null);
        resetUserIdentification();
      }
      setLoading(false);
    });

    return () => unsubscribeAuth && unsubscribeAuth();
  }, []);

  // Sync delivery address to localStorage whenever user profile changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    const delivery = userProfile?.address?.delivery;
    if (delivery) {
      const country = delivery.country ?? "";
      const zip = delivery.zipCode ?? "";
      const houseNumber = delivery.houseNumber ?? "";
      const houseNumberAddition = delivery.addition ?? "";
      const street = delivery.street ?? "";
      const city = delivery.city ?? "";
      localStorage.setItem(STORAGE_DELIVERY_COUNTRY, country);
      localStorage.setItem(STORAGE_DELIVERY_ZIP, String(zip));
      localStorage.setItem(STORAGE_DELIVERY_HOUSENUMBER, String(houseNumber));
      localStorage.setItem(
        STORAGE_DELIVERY_HOUSENUMBER_ADDITION,
        String(houseNumberAddition || ""),
      );
      localStorage.setItem(STORAGE_DELIVERY_STREET, street);
      localStorage.setItem(STORAGE_DELIVERY_CITY, city);

      updateUserProperties({
        delivery_city: city,
        delivery_country: country,
        delivery_zip: zip,
      });
    }
  }, [userProfile]);

  return (
    <AuthContext.Provider value={{ authUser, userProfile, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
